import styled, { css } from 'styled-components';
import {
  DateSort,
  EmptyData,
  Table,
  TextSort,
  useStyledTheme,
  TableColumn,
  TableStyles,
} from '@equifinance/shared';
import { DateFormatStringEnum, formatDateTime } from '@equifinance/shared/utils';
import { useMemo } from 'react';
import Section from '../../../Account/components/Section';
import { AuditRecord, useAuditContext } from '../../../../contexts/audit';
import ExpandableContent from './ExpandableContent';
import {
  AuditHistoryCell,
  AuditHistoryExpandableCell,
  PerformedByCell,
  TableSubHeader,
} from './AuditHistoryTableItems';
import { AuditHistoryRefName } from '../../AuditSidebar';

const collapsibleHeaderCss = css`${({ theme }) => `
  background: ${theme.colours.warning[3]};
  height: ${theme.collapseHeaderHeight};
  color: ${theme.colours.neutral[11]};
`}`;

const collapsibleBodyCss = css`${({ theme }) => `
  padding: ${theme.padding[6]};
  color: ${theme.colours.neutral[2]};
`}`;

const SectionNote = styled.p`${({ theme }) => `
  font-size: ${theme.fontSize[1]};
  font-style: italic;
  margin-bottom: ${theme.padding[5]};
`}`;

const mutateSearchValue = (value: string) => {
  const allowedChars = /[A-Za-z0-9\-\\/ ]/g;
  const filtered = value.match(allowedChars);
  return filtered ? filtered.join('') : '';
};

export default function AuditHistorySection() {
  const theme = useStyledTheme();
  const { auditHistory } = useAuditContext();

  const columns: TableColumn<AuditRecord>[] = useMemo(() => [
    {
      name: 'Field',
      cell: (auditRecord) => AuditHistoryCell(auditRecord.fieldDisplayName),
      sortable: true,
      sortFunction: TextSort('fieldDisplayName'),
    },
    {
      name: 'Old value',
      cell: (auditRecord) => AuditHistoryCell(auditRecord.oldValue || '-'),
      sortable: true,
      sortFunction: TextSort('oldValue'),
    },
    {
      name: 'New value',
      cell: (auditRecord) => AuditHistoryCell(auditRecord.newValue || '-'),
      sortable: true,
      sortFunction: TextSort('newValue'),
    },
    {
      name: 'Performed by',
      cell: (auditRecord) => PerformedByCell(auditRecord),
      sortable: true,
      sortFunction: (a, b) => {
        if (a.actionByCompanyName === b.actionByCompanyName) {
          return TextSort('actionByEmailAddress')(a, b);
        }
        return TextSort('actionByCompanyName')(a, b);
      },
    },
    {
      name: 'Timestamp',
      cell: (auditRecord) => AuditHistoryCell(formatDateTime(auditRecord.moment, DateFormatStringEnum.YYYYMMDDHHmmss)),
      sortable: true,
      sortFunction: DateSort('moment'),
    },
  ], [auditHistory]);

  const tableStyles: TableStyles = useMemo(() => ({
    cells: {
      style: {
        fontSize: theme.fontSize[1],
        '& > div': {
          'width': '100%',
        },
      },
    },
    headRow: {
      style: {
        borderRadius: `${theme.padding[2]} ${theme.padding[2]} 0 0`,
      },
    },
    rows: {
      style: {
        '&:last-of-type': {
          borderRadius: `0 0 ${theme.padding[2]} ${theme.padding[2]}`,
        },
        '&:not(:last-of-type)': {
          borderBottomStyle: 'none',
        },
      },
    },
  }), [theme]);

  return (
    <Section
      id={auditHistory?.applicationId}
      title="Audit History"
      refName={AuditHistoryRefName}
      headerCss={collapsibleHeaderCss}
      bodyCss={collapsibleBodyCss}
      titleIcon={false}
    >
      <SectionNote>Internal only</SectionNote>
      <Table
        columns={columns}
        data={auditHistory?.auditRecord || []}
        styles={tableStyles}
        SubHeaderContainer={TableSubHeader}
        ExpandableCell={AuditHistoryExpandableCell}
        expandableRowsComponent={ExpandableContent}
        defaultSortFieldId={5}
        defaultSortAsc={false}
        noDataComponent={<EmptyData message="" />}
        pagination
        searchInputProps={{
          mutateValue: mutateSearchValue,
        }}
        paginationComponentOptions={{
          rowsPerPageText: 'Sessions per page',
        }}
      />
    </Section>
  );
}
